import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { Op } from 'quill/core';
import React, { useEffect, useRef, useState } from 'react';
import * as styles from './CodeEmbedPreview.module.scss';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import LoadingIndicator from '../../components/LoadingIndicator';
import { UIActions } from '../../ui/uiSlice';
import EditorModal from '../modals/EditorModal';
import type { Blot } from '../types';
import { useUIElementResize } from '../UIElementResizeContext';
import useElementSizeSelector from '../utils/useElementSizeSelector';

export type PreviewRenderer = {
  allowFullScreen: boolean;
  renderToHTML: (code: string, width?: number) => Promise<string>;
};

interface CodeEmbedPreviewProps {
  blot: Blot;
  content: Op[];
  blotCommentIds: string[];
  setIsPreviewing: (isPreviewing: boolean) => void;
  previewRenderer: PreviewRenderer;
}

const CodeEmbedPreview = ({
  blot,
  content,
  blotCommentIds,
  setIsPreviewing,
  previewRenderer,
}: CodeEmbedPreviewProps) => {
  const dispatch = useAppDispatch();
  const onResize = useUIElementResize();

  const [setElement, width] = useElementSizeSelector((rect) => rect.width);
  const [lastCompiled, setLastCompiled] = useState<string | null>(null);
  const compiledRef = useRef(false);

  useEffect(() => onResize(), [lastCompiled, onResize]);
  useEffect(() => () => onResize(), [onResize]);

  useEffect(() => {
    const node = blot.domNode as HTMLElement;
    const commentClassnames = content
      .flatMap((op) => _.toArray(op.attributes?.comment))
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      .map((id) => `comment-${id}`);

    commentClassnames.forEach((classname) => node.classList.add(classname));
    return () => {
      commentClassnames.forEach((classname) =>
        node.classList.remove(classname),
      );
    };
  }, [content, blot, blotCommentIds]);

  const code = content
    .map((op) => (typeof op.insert === 'string' ? op.insert : ''))
    .join('');

  useEffect(() => {
    if (typeof width !== 'number') return;
    previewRenderer.renderToHTML(code, width).then(
      (previewCode) => {
        compiledRef.current = true;
        setLastCompiled(previewCode);
      },
      () => {
        if (!compiledRef.current) {
          setIsPreviewing(false);
        }
      },
    );
  }, [code, setIsPreviewing, previewRenderer, width]);

  return (
    <div
      className={classNames('code-embed-preview', styles.container)}
      onDoubleClick={() => {
        if (
          previewRenderer.allowFullScreen &&
          typeof lastCompiled === 'string'
        ) {
          dispatch(
            UIActions.openModal(EditorModal, {
              code: { compiledHTML: lastCompiled },
            }),
          );
        }
      }}
    >
      <div ref={setElement} className={styles.content}>
        {typeof lastCompiled === 'string' ? (
          <div
            className={styles.codePreview}
            dangerouslySetInnerHTML={{ __html: lastCompiled }}
          />
        ) : (
          <div className={styles.loading}>
            <LoadingIndicator />
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeEmbedPreview;
